<template>
  <!--
    Tabla de certificaciones agrupadas por orden de pedido (extraída de
    certificacion-mecanico / certificacion-electrico). Encapsula la <b-table>,
    el detalle expandible y las acciones de fila (regenerar / ver documento /
    retirar) junto con el modal de confirmación de retiro.

    Comunicación:
      Props:
        certifications → filas ya filtradas a mostrar (displayedCertifications del padre)
        loading        → estado de carga (isLoadingGrouped del padre)
        projectId      → necesario para las llamadas de regenerar/retirar
      Eventos:
        @view-current (row) → el padre abre el modal "Ver certificación actual"
        @reload             → tras regenerar/retirar con éxito, el padre recarga los datos
  -->
  <div>
    <!-- Tabla con estilo genérico compartido (epc-data-table). -->
    <div class="epc-data-table">
      <!-- Header genérico (mismo estilo que work-orders): título + conteo + acciones. -->
      <div class="epc-data-table__summary">
        <div class="epc-data-table__summary-title">
          <b-icon pack="fas" icon="certificate" size="is-small" />
          <span>{{ $t('certifications_list') }}</span>
          <span class="epc-data-table__count">{{ certifications.length }}</span>
        </div>
        <div v-if="$slots.actions" class="flex gap-2">
          <slot name="actions" />
        </div>
      </div>

      <section class="epc-data-table__body">
        <b-table
          :data="certifications"
          :loading="loading"
          detailed
          detail-key="id"
          :opened-detailed="openedGroupIds"
          @update:openedDetailed="openedGroupIds = $event"
          hoverable
          striped
          icon-pack="fas"
          class="text-sm"
        >
          <b-table-column field="id" label="ID" width="80" v-slot="props">
            <span class="text-blue font-medium">{{ props.row.id }}</span>
          </b-table-column>

          <b-table-column field="partner_name" :label="$t('subcontractor')" v-slot="props">
            {{ props.row.partner_name }}
          </b-table-column>

          <b-table-column field="work_order_code" :label="$t('order_number')" width="140" v-slot="props">
            {{ props.row.work_order_code }}
          </b-table-column>

          <b-table-column field="work_order_description" :label="$t('description')" v-slot="props">
            {{ props.row.work_order_description }}
          </b-table-column>

          <b-table-column field="order_amount" :label="$t('amount')" numeric width="120" v-slot="props">
            {{ formatCurrency(props.row.order_amount, props.row.currency) }}
          </b-table-column>

          <b-table-column field="certified_amount" :label="$t('certified')" numeric width="120" v-slot="props">
            {{ formatCurrency(props.row.certified_amount, props.row.currency) }}
          </b-table-column>

          <b-table-column field="pending_amount" :label="$t('pending')" numeric width="120" v-slot="props">
            {{ formatCurrency(props.row.pending_amount, props.row.currency) }}
          </b-table-column>

          <b-table-column field="progress_percentage" :label="$t('progress')" centered width="180" v-slot="props">
            <epc-progress-bar :value="props.row.progress_percentage" />
          </b-table-column>

          <b-table-column field="certifications_count" :label="$t('number')" centered width="80" v-slot="props">
            {{ props.row.certifications_count }}
          </b-table-column>

          <b-table-column field="actions" label="" width="60" centered v-slot="props">
            <b-tooltip :label="$t('view_current_certification')" position="is-top" append-to-body>
              <b-button
                type="is-text"
                icon-left="eye"
                icon-pack="fas"
                class="cert-action-btn cert-action-btn--blue"
                @click="viewCurrentForGroup(props.row)"
              ></b-button>
            </b-tooltip>
          </b-table-column>

          <template #detail="props">
            <div class="px-3 py-2 bg-gray-50">
              <table class="w-full text-xs border border-gray-200">
                <thead class="bg-gray-100">
                  <tr>
                    <th class="px-2 py-1 text-left">{{ $t('certification') }}</th>
                    <th class="px-2 py-1 text-left">{{ $t('date_range') }}</th>
                    <th class="px-2 py-1 text-left">{{ $t('created_at') }}</th>
                    <th class="px-2 py-1 text-right">{{ $t('to_certify') }}</th>
                    <th class="px-2 py-1 text-right">{{ $t('previous') }}</th>
                    <th class="px-2 py-1 text-right">{{ $t('accumulated') }}</th>
                    <th class="px-2 py-1 text-right">{{ $t('pending') }}</th>
                    <th class="px-2 py-1 text-center">{{ $t('progress') }}</th>
                    <th class="px-2 py-1 text-center">{{ $t('status') }}</th>
                    <th class="px-2 py-1 text-center" style="width:110px;"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!props.row.certifications || props.row.certifications.length === 0">
                    <td colspan="9" class="px-2 py-3 text-center text-gray-500">{{ $t('no_results') }}</td>
                  </tr>
                  <tr
                    v-for="cert in (props.row.certifications || [])"
                    :key="`g-${props.row.id}-c-${cert.id}`"
                    class="border-t border-gray-100 cert-detail-row"
                    :class="{
                      'cert-detail-row--processing': isRegeneratingCertificationId === cert.id || isRecallingCertificationId === cert.id,
                      'cert-detail-row--recalling': isRecallingCertificationId === cert.id,
                    }"
                  >
                    <td class="px-2 py-1 font-medium">
                      <!-- El nombre de la cert enlaza a su flujo de validación (cert.id = validation_flow_id). -->
                      <a
                        class="text-blue-dark hover:underline cursor-pointer"
                        :title="$t('go_to_validation_flow')"
                        @click="goToFlow(cert)"
                      >{{ cert.certification }}</a>
                    </td>
                    <td class="px-2 py-1">
                      <span v-if="cert.date_from && cert.date_to">
                        {{ formatDate(cert.date_from) }} - {{ formatDate(cert.date_to) }}
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td class="px-2 py-1">{{ formatDate(cert.created_at) }}</td>
                    <td class="px-2 py-1 text-right">{{ formatCurrency(cert.to_certify, props.row.currency) }}</td>
                    <td class="px-2 py-1 text-right">{{ formatCurrency(cert.previous, props.row.currency) }}</td>
                    <td class="px-2 py-1 text-right">{{ formatCurrency(cert.accumulated, props.row.currency) }}</td>
                    <td class="px-2 py-1 text-right">{{ formatCurrency(cert.pending, props.row.currency) }}</td>
                    <td class="px-2 py-1 text-center">
                      <epc-progress-bar :value="cert.percent" />
                    </td>
                    <td class="px-2 py-1 text-center">
                      <validation-status-label :status="cert.status" style="vertical-align: middle" />
                    </td>
                    <td class="px-2 py-1 text-center whitespace-nowrap cert-actions-cell">
                      <!-- Estado en curso: bloquea la fila y muestra "retirando/generando" -->
                      <div
                        v-if="isRecallingCertificationId === cert.id || isRegeneratingCertificationId === cert.id"
                        class="cert-row-status"
                        :class="isRecallingCertificationId === cert.id ? 'cert-row-status--danger' : 'cert-row-status--blue'"
                      >
                        <b-icon pack="fas" icon="spinner" custom-class="fa-spin" size="is-small"></b-icon>
                        <span>{{ isRecallingCertificationId === cert.id ? $t('recalling') : $t('regenerating') }}</span>
                      </div>
                      <!-- Acciones (iconos redondos con hover tipo Google) -->
                      <div v-else class="cert-actions">
                        <b-tooltip :label="$t('regenerate')" position="is-top" append-to-body>
                          <b-button
                            type="is-text"
                            icon-left="redo-alt"
                            icon-pack="fas"
                            class="cert-action-btn cert-action-btn--blue"
                            @click="regenerateCertification(cert)"
                          ></b-button>
                        </b-tooltip>
                        <b-tooltip :label="$t('view_document')" position="is-top" append-to-body>
                          <b-button
                            type="is-text"
                            icon-left="file-alt"
                            icon-pack="fas"
                            class="cert-action-btn cert-action-btn--blue"
                            @click="viewCertification(cert)"
                          ></b-button>
                        </b-tooltip>
                        <b-tooltip :label="$t('recall')" position="is-top" append-to-body>
                          <b-button
                            type="is-text"
                            icon-left="undo"
                            icon-pack="fas"
                            class="cert-action-btn cert-action-btn--danger"
                            :disabled="!canRecallCertification(cert)"
                            @click="recallCertification(cert)"
                          ></b-button>
                        </b-tooltip>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <template #empty>
            <div class="has-text-centered py-8">
              <p class="text-gray-500">{{ $t('no_results') }}</p>
            </div>
          </template>
        </b-table>
      </section>
    </div>

    <!-- Modal de confirmación de retiro (EpcModalWrapper declarativo; chrome del wrapper). -->
    <epc-modal-wrapper
      :active.sync="recallDialog.active"
      :title="$t('recall_certification')"
      :width="440"
      :can-cancel="['escape', 'outside']"
    >
      <p v-if="recallDialog.cert" class="inline-flex items-center gap-1 mb-3 px-3 py-1 rounded-full bg-indigo-lightest text-blue-dark text-sm font-semibold">
        <b-icon pack="fas" icon="certificate" size="is-small" />
        <span>{{ recallDialog.cert.certification || '-' }}</span>
      </p>
      <p class="mb-2 text-sm text-gray-700">{{ $t('recall_certification_confirm') }}</p>

      <template #footer>
        <button type="button" class="epc-modal__button" @click="_CloseRecallDialog">
          {{ $t('cancel') }}
        </button>
        <button
          type="button"
          class="epc-modal__button epc-modal__button--primary"
          :class="{ 'epc-modal__button--disabled': isRecallingCertificationId !== null }"
          :disabled="isRecallingCertificationId !== null"
          @click="_ConfirmRecallDialog"
        >
          {{ $t('recall') }}
        </button>
      </template>
    </epc-modal-wrapper>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import validationStatusLabel from '·/components/validations/validation-status-label'
import EpcModalWrapper from '·/components/epc-modal-wrapper.vue'
import EpcProgressBar from './EpcProgressBar.vue'

export default {
  name: 'CertificationTable',

  components: {
    validationStatusLabel,
    EpcModalWrapper,
    EpcProgressBar,
  },

  props: {
    // Filas ya filtradas a mostrar (displayedCertifications del padre).
    certifications: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // Necesario para las llamadas de regenerar/retirar.
    projectId: {
      type: Number,
      default: null,
    },
  },

  data: () => ({
    openedGroupIds: [],
    isRegeneratingCertificationId: null,
    isRecallingCertificationId: null,
    recallDialog: { active: false, cert: null },
  }),

  methods: {
    formatDate(date) {
      return moment(date).format('DD/MM/YY')
    },
    // Navega al flujo de validación asociado a la certificación (cert.id = validation_flow_id).
    goToFlow(cert) {
      if (!cert || !cert.id) return
      this.$router.push({ name: 'validation.home', params: { validation_id: cert.id } })
    },
    // Símbolo de moneda por fila (currency del grupo); € por defecto. Antes estaba fijo en $.
    formatCurrency(value, currency = '€') {
      const sym = currency || '€'
      if (value == null) return '0 ' + sym
      return value.toLocaleString('es-ES', { minimumFractionDigits: 0 }) + ' ' + sym
    },
    // Prefill desde la fila del listado (partner + pedido del grupo): el padre abre el modal.
    viewCurrentForGroup(row) {
      if (!row || !row.partner_id || !row.work_order_id) return
      this.$emit('view-current', {
        partnerId: row.partner_id,
        orderId: row.work_order_id,
      })
    },
    // "Ver documento": abre el PDF real adjunto a la certificación (igual que obra civil).
    viewCertification(cert) {
      if (cert && cert.document_url) {
        window.open(cert.document_url, '_blank')
        return
      }
      this.$notify.warning('error_loading_data')
    },
    // "Volver a generar": regenera el PDF versionando el mismo documento (igual que obra civil)
    // y lo abre para ver los cambios.
    async regenerateCertification(cert) {
      const flowId = cert && cert.id // en la fila, id = validation_flow_id
      if (!flowId) {
        this.$notify.warning('error_loading_data')
        return
      }
      if (this.isRegeneratingCertificationId === flowId) {
        return
      }

      this.isRegeneratingCertificationId = flowId
      try {
        const { data } = await axios.post(
          `/api/v2/project/${this.projectId}/pictograms/certifications/${flowId}/regenerate`
        )
        if (data && data.success) {
          // regenerated === false → no había elementos certificables (Aceptado por
          // Calidad al 100%): no se generó nueva versión, solo se avisa.
          if (data.regenerated === false) {
            this.$notify.warning(data.message || 'no_certifiable_elements')
          } else {
            this.$notify.success('success')
            if (data.documentUrl) {
              window.open(data.documentUrl, '_blank')
            }
            this.$emit('reload', { silent: true })
          }
        } else {
          this.$notify.error(data && data.message ? data.message : 'error')
        }
      } catch (e) {
        // 403: sin permiso (backend exige admin o certificador) → mensaje claro localizado.
        if (e && e.response && e.response.status === 403) {
          this.$notify.error('certification_generate_not_authorized')
          return
        }
        const msg = e && e.response && e.response.data && e.response.data.message
        this.$notify.error(msg || 'error')
      } finally {
        this.isRegeneratingCertificationId = null
      }
    },
    // Bloqueado en COMPLETED (2), REJECTED (3), REJECTED_PARTIAL (7): no se puede retirar una
    // cert cuya tarea ya fue actuada (mismo criterio que obra civil).
    canRecallCertification(cert) {
      if (!cert || !cert.id) return false
      const blocked = [2, 3, 7]
      return !blocked.includes(Number(cert.status))
    },

    // "Retirar" (recall): abre el modal de confirmación (estilo del envío de emails de patterns).
    recallCertification(cert) {
      if (!this.canRecallCertification(cert)) return
      this.recallDialog = { active: true, cert }
    },

    _CloseRecallDialog() {
      if (this.isRecallingCertificationId !== null) return // no cerrar mientras se retira
      this.recallDialog.active = false
      this.recallDialog.cert = null
    },

    // Confirma el retiro: borra (soft-delete) el flujo de validación reutilizando el endpoint de
    // obra civil (es agnóstico de entidad). Al eliminar el flujo, el observer archiva el marcado
    // de los elementos certificados por esta cert, dejándolos listos para recertificar.
    async _ConfirmRecallDialog() {
      const cert = this.recallDialog.cert
      if (!cert || !this.canRecallCertification(cert)) return
      const flowId = cert.id // en la fila, id = validation_flow_id
      this.isRecallingCertificationId = flowId
      // Cerramos el modal de inmediato: la fila ya queda marcada como "retirando".
      this.recallDialog.active = false
      this.recallDialog.cert = null
      try {
        const { data } = await axios.post(
          `/api/v2/project/${this.projectId}/certifications/${flowId}/recall`
        )
        if (data && data.success) {
          this.$notify.success('certification_recalled')
          this.$emit('reload', { silent: true })
        } else {
          this.$notify.warning((data && data.error) || 'error')
        }
      } catch (e) {
        const msg = (e && e.response && e.response.data && e.response.data.error) || 'error'
        this.$notify.warning(msg)
      } finally {
        this.isRecallingCertificationId = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// La tabla usa la clase global genérica `.epc-data-table` (cabecera azul, celdas
// con borde, hover, striped, y el scroll horizontal flotante). Aquí solo los
// ajustes propios del detalle.

// Fila de detalle sin los bordes/padding de celda que aplica epc-data-table,
// para que el contenido expandido no se vea como una celda más.
::v-deep tr.detail > td {
  padding: 0 !important;
  border: none !important;
  background: #f9fafb;
}
::v-deep tr.detail .detail-container {
  padding: 0 !important;
}

/* Fila en procesamiento (regenerar/retirar): bloquea toda la fila + overlay */
.cert-detail-row--processing td {
  position: relative;
  pointer-events: none;
  background-color: rgba(249, 250, 251, 0.9);
}
.cert-detail-row--processing td::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.45);
  pointer-events: none;
  z-index: 0;
}
.cert-detail-row--processing td .button,
.cert-detail-row--processing td .cert-row-status {
  position: relative;
  z-index: 1;
}
/* Al retirar, un tinte rojo suave para reforzar el estado destructivo */
.cert-detail-row--recalling td {
  background-color: rgba(254, 242, 242, 0.9);
}

/* Badge de estado en curso dentro de la fila ("Retirando…" / "Generando…") */
.cert-row-status {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}
.cert-row-status--danger { color: #dc2626; }
.cert-row-status--blue { color: #385da5; }

/* Acciones: iconos redondos con hover tipo Google (Material icon button) */
.cert-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
}
.cert-action-btn.button {
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #6b7280;
  box-shadow: none;
  transition: background-color 150ms ease, color 150ms ease, box-shadow 150ms ease;
}
.cert-action-btn.button:hover:not([disabled]) {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
.cert-action-btn--blue.button:hover:not([disabled]) {
  color: #385da5;
  background-color: rgba(85, 119, 187, 0.14);
}
.cert-action-btn--danger.button:hover:not([disabled]) {
  color: #dc2626;
  background-color: rgba(220, 38, 38, 0.14);
}
.cert-action-btn.button:focus:not(:active) {
  box-shadow: 0 0 0 3px rgba(85, 119, 187, 0.18);
}
.cert-action-btn.button[disabled] {
  opacity: 0.4;
  background: transparent;
}
</style>
